import { PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { AppState } from "../../../store";
import fetchAPI, { ApiResponse, HttpMethod } from "../../../utils/api";
import { hasRole } from "../../../utils/roles";
import bookingPageSlice, { Booking } from "../../booking-page/store/slice";
import routeSlice from "../../routes/store/slice";
import { showError, showSuccess } from "../../toaster/store/slice";
import userFiltersSlice from "../../user-filters/store/slice";
import { RoleCode, User } from "../../users/store/slice";
import { VehicleType, VehicleTypeCode } from "../../vehicle-types/store/slice";
import { BookingFormValues } from "../BookingForm";
import bookingFormSlice, { Schedule } from "./slice";
import log from "../../../utils/logger";

export type ScheduleRequest = {
  routeId: number;
  date: string;
};

export type AvailableDaysRequest = {
  routeId: number;
};

type BookingFormResponse = ApiResponse<{ data: Booking }>;
type BookingResponse = ApiResponse<{ data: Booking }>;
type ScheduleResponse = ApiResponse<{ data: Schedule }>;

type DriverCreateRequest = {
  first_name: string;
  last_name: string;
  phone?: string | null;
  birth_date: string | null;
  document_number: string;
  country_id: number | null;
  document_country_id: number | null;
  document_expiration_date: string | null;
  gender?: number | null;
};

type BookingCreateRequest = {
  user_id: number | null;
  route_id: number;
  departure_at: string;
  arrival_at: string;
  duration: string;
  vehicle_type_id: number;
  trailer_type_id: number | null;
  cargo_type_id: number | null;
  vehicle_number?: string;
  trailer_number?: string;
  vehicle_registration_country_id?: number | null;
  trailer_registration_country_id?: number | null;
  vehicle_length: number;
  vehicle_width: number;
  vehicle_weight: number;
  plug_in?: boolean;
  adr?: boolean;
  drivers: DriverCreateRequest[];
  customer_reference: string | null;
  temperature?: string;
  // document_ids: number[];
};

type BookingUpdateRequest = Omit<
  BookingCreateRequest,
  "route_id" | "user_id" | "departure_at" | "arrival_at" | "duration"
  // | "document_ids"
> & {
  departure_at?: string;
  arrival_at?: string;
  duration?: string;
  // documents_to_add_ids: number[];
  // documents_to_remove_ids: number[];
};

function* bookingFormSaga() {
  yield takeLatest(bookingFormSlice.actions.fetchData, handleFetch);
  yield takeLatest(bookingFormSlice.actions.submit, handleSubmit);
  yield takeLatest(bookingFormSlice.actions.update, handleUpdate);
  yield takeLatest(bookingFormSlice.actions.fetchSchedule, handleSchedules);
  yield takeLatest(
    bookingFormSlice.actions.fetchAvailableDays,
    handleAvailableDays
  );
}

function* handleFetch({ payload: id }: PayloadAction<number>) {
  const response: BookingResponse = yield call(fetchAPI, `/bookings/${id}`);

  if (response && response.data) {
    const { data } = response;

    yield put(bookingFormSlice.actions.setData(data));

    yield put(routeSlice.actions.setData([data.route]));

    // if (data.documents.length > 0) {
    //   yield put(documentSlice.actions.addData(data.documents));
    // }
  }

  yield put(bookingFormSlice.actions.setIsFetching(false));
}

function* handleSubmit({ payload }: PayloadAction<BookingFormValues>) {
  const profile: User = yield select(
    (state: AppState) => state.loginPage.profile
  );
  // const documents: Media[] = yield select(
  //   (state: AppState) => state.documents.data
  // );

  if (!payload.route) {
    throw new Error("Route not found.");
  }

  const schedules: Schedule[] = yield select(
    (state: AppState) => state.bookingForm.schedule
  );

  const schedule = schedules.find((time) => time.id === Number(payload.time));

  if (!schedule) {
    log.error("Schedule was not found while creating booking.");

    yield put(showError("Schedule not found. Please contact your manager."));

    return;
  }

  const departureDate = moment(payload.date).format("YYYY-MM-DD");

  const departure_at = `${departureDate} ${schedule.departure_time}`;

  const arrival_at = `${moment(payload.date)
    .add(schedule.arrival_day - schedule.departure_day, "days")
    .format("YYYY-MM-DD")} ${schedule.arrival_time}`;

  const duration = schedule.duration;

  const body: Omit<BookingCreateRequest, "user_id"> & {
    user_id?: number | null;
  } = {
    route_id: Number(payload.route.value),
    departure_at,
    arrival_at,
    duration,
    vehicle_type_id: Number(payload.vehicle_type?.value),
    trailer_type_id: payload.trailer_type?.value
      ? Number(payload.trailer_type?.value)
      : null,
    cargo_type_id: payload.cargo_type?.value
      ? Number(payload.cargo_type?.value)
      : null,
    vehicle_number: payload.vehicle_number,
    trailer_number: payload.trailer_number!,
    vehicle_registration_country_id: payload.vehicle_registration_country?.value
      ? Number(payload.vehicle_registration_country?.value)
      : null,
    trailer_registration_country_id: payload.trailer_registration_country?.value
      ? Number(payload.trailer_registration_country?.value)
      : null,
    vehicle_length: payload.vehicle_length!,
    vehicle_width: payload.vehicle_width!,
    vehicle_weight: payload.vehicle_weight!,
    plug_in: payload.plug_in,
    adr: payload.adr,
    drivers: payload.drivers.map((driver) => ({
      first_name: driver.first_name,
      last_name: driver.last_name,
      phone: driver.phone ?? null,
      birth_date: driver.birth_date
        ? moment(driver.birth_date).format("YYYY-MM-DD")
        : null,
      document_number: driver.document_number,
      country_id: driver.country?.value ? Number(driver.country?.value) : null,
      document_country_id: driver.document_country?.value
        ? Number(driver.document_country?.value)
        : null,
      document_expiration_date: driver.document_expiration_date
        ? moment(driver.document_expiration_date).format("YYYY-MM-DD")
        : null,
      gender: driver.gender,
    })),
    customer_reference: payload.customer_reference,
    temperature: payload.temperature,
    // document_ids: documents.map((document) => document.id),
  };

  if (payload.customer && hasRole(profile, RoleCode.Internal)) {
    body["user_id"] = Number(payload.customer.value);
  }

  const response: BookingFormResponse = yield call(fetchAPI, "/bookings", {
    method: HttpMethod.POST,
    body: JSON.stringify(body),
  });

  if (response && response.data) {
    yield put(showSuccess("Booking registered."));

    yield put(bookingPageSlice.actions.resetData());
    yield put(bookingPageSlice.actions.fetchData());

    yield put(userFiltersSlice.actions.fetchData());
    yield put(bookingFormSlice.actions.setModalMustBeClosed(true));
  }

  yield put(bookingFormSlice.actions.setIsLoading(false));
}

function* handleUpdate({
  payload: { id, data },
}: PayloadAction<{ id: number; data: BookingFormValues }>) {
  const schedules: Schedule[] = yield select(
    (state: AppState) => state.bookingForm.schedule
  );

  const schedule = schedules.find((time) => time.id === Number(data.time));

  const departureDate = `${moment(data.date).format("YYYY-MM-DD")}`;

  const departure_at = schedule
    ? `${departureDate} ${schedule.departure_time}`
    : null;

  const arrival_at = schedule
    ? `${moment(data.date)
        .add(schedule.arrival_day - schedule.departure_day, "days")
        .format("YYYY-MM-DD")} ${schedule.arrival_time}`
    : null;

  const duration = schedule ? schedule.duration : null;

  // const booking: Booking = yield select(
  //   (state: AppState) => state.bookingForm.data
  // );
  // const documents: Media[] = yield select(
  //   (state: AppState) => state.documents.data
  // );

  // const addedDocuments = documents.filter(
  //   (document) =>
  //     !booking.documents.find(
  //       (originalDocument) => originalDocument.id === document.id
  //     )
  // );
  // const removedDocuments = booking.documents.filter(
  //   (originalDocument) =>
  //     !documents.find((document) => document.id === originalDocument.id)
  // );

  const body: BookingUpdateRequest = {
    vehicle_type_id: Number(data.vehicle_type?.value),
    trailer_type_id: data.trailer_type?.value
      ? Number(data.trailer_type?.value)
      : null,
    cargo_type_id: data.cargo_type?.value
      ? Number(data.cargo_type?.value)
      : null,
    vehicle_number: data.vehicle_number,
    trailer_number: data.trailer_number,
    vehicle_registration_country_id: data.vehicle_registration_country?.value
      ? Number(data.vehicle_registration_country?.value)
      : null,
    trailer_registration_country_id: data.trailer_registration_country?.value
      ? Number(data.trailer_registration_country?.value)
      : null,
    vehicle_length: data.vehicle_length!,
    vehicle_width: data.vehicle_width!,
    vehicle_weight: data.vehicle_weight!,
    plug_in: data.plug_in,
    adr: data.adr,
    drivers: data.drivers.map((driver) => ({
      first_name: driver.first_name,
      last_name: driver.last_name,
      phone: driver.phone ?? null,
      birth_date: driver.birth_date
        ? moment(driver.birth_date).format("YYYY-MM-DD")
        : null,
      document_number: driver.document_number,
      country_id: driver.country?.value ? Number(driver.country?.value) : null,
      document_country_id: driver.document_country?.value
        ? Number(driver.document_country?.value)
        : null,
      document_expiration_date: driver.document_expiration_date
        ? moment(driver.document_expiration_date).format("YYYY-MM-DD")
        : null,
      gender: driver.gender,
    })),
    customer_reference: data.customer_reference,
    temperature: data.temperature,
    // documents_to_add_ids: addedDocuments.map((document) => document.id),
    // documents_to_remove_ids: removedDocuments.map((document) => document.id),
  };

  if (departure_at && arrival_at && duration) {
    body.departure_at = departure_at;
    body.arrival_at = arrival_at;
    body.duration = duration;
  }

  const vehicleTypes: VehicleType[] = yield select(
    (state: AppState) => state.vehicleTypes.data
  );
  const trailerTypes = vehicleTypes.filter((vehicleType) =>
    [VehicleTypeCode.Trailer, VehicleTypeCode.TrailerEmpty].includes(
      vehicleType.code
    )
  );
  const hasTrailerType = trailerTypes.some(
    (trailerType) => trailerType.id === Number(data.vehicle_type?.value)
  );

  if (!hasTrailerType) {
    body.vehicle_number = data.vehicle_number;
  }

  const response: BookingFormResponse = yield call(
    fetchAPI,
    `/bookings/${id}`,
    {
      method: HttpMethod.PUT,
      body: JSON.stringify(body),
    }
  );

  if (response && response.data) {
    yield put(showSuccess("Booking updated."));

    yield put(bookingPageSlice.actions.resetData());
    yield put(bookingPageSlice.actions.fetchData());
  }

  yield put(bookingFormSlice.actions.setIsLoading(false));
}

function* handleSchedules({
  payload: { routeId, date },
}: PayloadAction<ScheduleRequest>) {
  const response: ScheduleResponse = yield call(
    fetchAPI,
    `/routes/${routeId}/schedule?date=${date}`
  );

  if (response && response.data) {
    yield put(bookingFormSlice.actions.setSchedule(response.data));
  }

  yield put(bookingFormSlice.actions.setIsScheduleFetching(false));
}

function* handleAvailableDays({
  payload: { routeId },
}: PayloadAction<AvailableDaysRequest>) {
  const response: ScheduleResponse = yield call(
    fetchAPI,
    `/routes/${routeId}/schedule/available-days`
  );

  if (response && response.data) {
    yield put(bookingFormSlice.actions.setAvailableDays(response.data));
  }

  yield put(bookingFormSlice.actions.setIsScheduleFetching(false));
}

export default bookingFormSaga;
